/**
 * Frontend Unit Conversion Utility
 * 
 * Converts values for display based on user preferences.
 * All values are stored in base units and converted for display.
 * 
 * Base Units:
 * - Fuel: kg
 * - Altitude: ft
 * - Temperature: °C
 * - Pressure: PSI
 * - Speed: knots
 */

/**
 * Convert fuel value
 */
export function convertFuel(value, fromUnit, toUnit) {
  if (!value || fromUnit === toUnit) return value;
  
  // Convert to base unit (kg) first
  let baseValue = value;
  if (fromUnit === 'lb') {
    baseValue = value * 0.453592; // lb to kg
  }
  
  // Convert from base unit to target unit
  if (toUnit === 'lb') {
    return baseValue / 0.453592; // kg to lb
  }
  
  return baseValue; // kg
}

/**
 * Convert altitude value
 */
export function convertAltitude(value, fromUnit, toUnit) {
  if (!value || fromUnit === toUnit) return value;
  
  // Convert to base unit (ft) first
  let baseValue = value;
  if (fromUnit === 'm') {
    baseValue = value * 3.28084; // m to ft
  }
  
  // Convert from base unit to target unit
  if (toUnit === 'm') {
    return baseValue / 3.28084; // ft to m
  }
  
  return baseValue; // ft
}

/**
 * Convert temperature value
 */
export function convertTemperature(value, fromUnit, toUnit) {
  if (!value || fromUnit === toUnit) return value;
  
  // Convert to base unit (°C) first
  let baseValue = value;
  if (fromUnit === '°F') {
    baseValue = (value - 32) * 5/9; // °F to °C
  }
  
  // Convert from base unit to target unit
  if (toUnit === '°F') {
    return (baseValue * 9/5) + 32; // °C to °F
  }
  
  return baseValue; // °C
}

/**
 * Convert pressure value
 */
export function convertPressure(value, fromUnit, toUnit) {
  if (!value || fromUnit === toUnit) return value;
  
  // Convert to base unit (PSI) first
  let baseValue = value;
  if (fromUnit === 'kPa') {
    baseValue = value * 0.145038; // kPa to PSI
  }
  
  // Convert from base unit to target unit
  if (toUnit === 'kPa') {
    return baseValue / 0.145038; // PSI to kPa
  }
  
  return baseValue; // PSI
}

/**
 * Convert speed value
 */
export function convertSpeed(value, fromUnit, toUnit) {
  if (!value || fromUnit === toUnit) return value;
  
  // Convert to base unit (knots) first
  let baseValue = value;
  if (fromUnit === 'km/h') {
    baseValue = value * 0.539957; // km/h to knots
  }
  
  // Convert from base unit to target unit
  if (toUnit === 'km/h') {
    return baseValue / 0.539957; // knots to km/h
  }
  
  return baseValue; // knots
}

/**
 * Convert value from display unit back to base unit for storage
 */
export function convertToBaseUnit(value, fieldType, displayUnit) {
  if (value === null || value === undefined) return value;
  
  const baseUnits = {
    FUEL: 'kg',
    ALTITUDE: 'ft',
    TEMPERATURE: '°C',
    PRESSURE: 'PSI',
    SPEED: 'knots'
  };
  
  const baseUnit = baseUnits[fieldType];
  if (displayUnit === baseUnit) return value;
  
  switch (fieldType) {
    case 'FUEL':
      return convertFuel(value, displayUnit, baseUnit);
    case 'ALTITUDE':
      return convertAltitude(value, displayUnit, baseUnit);
    case 'TEMPERATURE':
      return convertTemperature(value, displayUnit, baseUnit);
    case 'PRESSURE':
      return convertPressure(value, displayUnit, baseUnit);
    case 'SPEED':
      return convertSpeed(value, displayUnit, baseUnit);
    default:
      return value;
  }
}

/**
 * Convert value for display based on unit preference
 */
export function convertForDisplay(value, fieldType, unitPreference) {
  if (value === null || value === undefined) return value;
  
  const baseUnits = {
    FUEL: 'kg',
    ALTITUDE: 'ft',
    TEMPERATURE: '°C',
    PRESSURE: 'PSI',
    SPEED: 'knots'
  };
  
  const baseUnit = baseUnits[fieldType];
  const displayUnit = unitPreference || baseUnit;
  
  if (displayUnit === baseUnit) return value;
  
  switch (fieldType) {
    case 'FUEL':
      return convertFuel(value, baseUnit, displayUnit);
    case 'ALTITUDE':
      return convertAltitude(value, baseUnit, displayUnit);
    case 'TEMPERATURE':
      return convertTemperature(value, baseUnit, displayUnit);
    case 'PRESSURE':
      return convertPressure(value, baseUnit, displayUnit);
    case 'SPEED':
      return convertSpeed(value, baseUnit, displayUnit);
    default:
      return value;
  }
}

/**
 * Format unit label for display
 */
export function formatUnitLabel(fieldType, unit) {
  const labels = {
    FUEL: { kg: 'kg', lb: 'lb' },
    ALTITUDE: { ft: 'ft', m: 'm' },
    TEMPERATURE: { '°C': '°C', '°F': '°F' },
    PRESSURE: { PSI: 'PSI', kPa: 'kPa' },
    SPEED: { knots: 'knots', 'km/h': 'km/h' }
  };
  
  return labels[fieldType]?.[unit] || unit || '';
}
